import {
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  OnInit,
  ViewChild,
  computed,
  inject,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';

import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Subscription, lastValueFrom } from 'rxjs';
import { Auth, GroupSwitchIncompleteError } from '../../../core/auth/auth';
import { CommonService } from '../../../core/services/common';
import { Notifications } from '../../../shared/components/notifications/notifications';
import { Languageswitcher } from '../../../shared/components/languageswitcher/languageswitcher';
import { Fullscreen } from '../../../shared/components/fullscreen/fullscreen';
import { Helpicon } from '../../../shared/components/helpicon/helpicon';
import { UserProfile } from '../../../shared/components/userprofile/userprofile';
import { AvatarRole } from '../../../shared/components/avatar/avatar';
import { SidebarService } from '../../../core/services/sidebar';
import { ThemeSwitcher } from '@shared/components/themeswitcher/themeswitcher';
import { BulkUploadSocketService } from '@core/services/bulk-upload-socket.service';
import { NotificationsService } from '@core/services/notifications';
import { ToastService } from '@core/services/toaster';
import { Logger } from '@core/services/logger';
import { CompanySwitcher } from './company-switcher/company-switcher';
import { ConfirmSwitchModal, SwitchCard } from './company-switcher/confirm-switch-modal/confirm-switch-modal';
import { AccessSwitcher, AccessSwitchRequest } from './access-switcher/access-switcher';
import {
  CompanySwitchRequest,
  GenerateTokenRequest,
  SwitchableCompanyView,
} from '../../../models/company-switcher.model';

/** The fields of a permission-filtered menu row (menuList$) the global search reads. */
interface MenuItem {
  id: number;
  name: string;
  link?: string | null;
  parentID?: number | null;
  can_view?: boolean;
}

/** A global-search hit: the menu plus its parent's name, for disambiguation. */
interface SearchResult extends MenuItem {
  parentName: string | null;
}

/**
 * Link prefixes kept out of global search. Employee List's children (Family &
 * Address, Personal Information, Documents, ...) are tabs inside one
 * employee's record, held as menus only for permissions - opened from search
 * they land on a tab with no employee selected.
 */
const SEARCH_EXCLUDED_LINK_PREFIXES: readonly string[] = ['/ess/employee/employee/'];

function isExcludedFromSearch(linkKey: string): boolean {
  return SEARCH_EXCLUDED_LINK_PREFIXES.some((prefix) => linkKey.startsWith(prefix));
}

/** A menu link normalised for comparison: lower-case, one leading '/', no trailing '/'. */
function toLinkKey(link: string | null | undefined): string {
  return '/' + trimSlashes(String(link ?? '').trim().toLowerCase());
}

/**
 * True for a sub-menu that opens the same screen as its parent. Data Upload /
 * Download's ~30 sub-menus (Shift Roaster, Employee Bank, ...) all open the
 * parent's bulk-upload screen, so they crowded out real screens with the same
 * name (e.g. "Shift" matched "Shift Roaster" before "Work Shift"). Only the
 * sub-menus are skipped - the parent itself stays searchable.
 */
function sharesParentLink(menu: MenuItem, linkKey: string, byId: ReadonlyMap<number, MenuItem>): boolean {
  if (menu.parentID == null || menu.parentID === menu.id) return false;
  const parent = byId.get(menu.parentID);
  return !!parent?.link && toLinkKey(parent.link) === linkKey;
}

/**
 * False when the menu or any loaded ancestor has can_view === false. The
 * sidebar never renders a child under a parent the user can't view, so search
 * mustn't offer it either. Bounded so a parentID cycle in the data can't loop.
 */
function isViewable(menu: MenuItem, byId: ReadonlyMap<number, MenuItem>): boolean {
  let current: MenuItem | undefined = menu;
  for (let hops = 0; current && hops <= MAX_SEARCH_MENU_DEPTH; hops++) {
    if (current.can_view === false) return false;
    if (current.parentID == null || current.parentID === current.id) break;
    current = byId.get(current.parentID);
  }
  return true;
}

/**
 * Menu levels: 1 = Primary, 2 = Secondary, 3 = Tertiary. Global search lists
 * Primary and Secondary menus only - Tertiary ones are sub-items of a screen.
 */
const MAX_SEARCH_MENU_DEPTH = 2;

/**
 * A menu's level, by walking parentID up the loaded list. A menu whose
 * parentID is empty or its own id is top-level - the same rule the sidebar
 * uses. Stops at a parent that isn't loaded, and at MAX_SEARCH_MENU_DEPTH + 1
 * so a longer parentID cycle in the data can't loop forever.
 */
function menuDepth(menu: MenuItem, byId: ReadonlyMap<number, MenuItem>): number {
  let depth = 1;
  let current = menu;
  while (current.parentID != null && current.parentID !== current.id && depth <= MAX_SEARCH_MENU_DEPTH) {
    const parent = byId.get(current.parentID);
    if (!parent) break;
    depth++;
    current = parent;
  }
  return depth;
}

/** Strips leading/trailing '/' - a plain scan, no backtracking regex (Sonar S8786). */
function trimSlashes(value: string): string {
  let start = 0;
  let end = value.length;
  while (start < end && value[start] === '/') start++;
  while (end > start && value[end - 1] === '/') end--;
  return value.slice(start, end);
}

@Component({
  selector: 'app-header',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    Notifications,
    Languageswitcher,
    Fullscreen,
    Helpicon,
    UserProfile,
    CompanySwitcher,
    ConfirmSwitchModal,
    AccessSwitcher,
  ],
  templateUrl: './header.html',
  styleUrl: './header.scss',
})
export class Header implements OnInit, OnDestroy {
  @ViewChild(Notifications) notificationsRef!: Notifications;
  @ViewChild(UserProfile) userProfileRef!: UserProfile;
  @ViewChild(CompanySwitcher) companySwitcherRef!: CompanySwitcher;
  @ViewChild(AccessSwitcher) accessSwitcherRef?: AccessSwitcher;

  private readonly auth = inject(Auth);
  private readonly router = inject(Router);
  private readonly commonService = inject(CommonService);
  private readonly modalService = inject(NgbModal);
  private readonly sidebarService = inject(SidebarService);
  private readonly bulkSocket = inject(BulkUploadSocketService);
  private readonly notifyService = inject(NotificationsService);
  private readonly toaster = inject(ToastService);
  private readonly logger = inject(Logger);
  private readonly elementRef = inject(ElementRef);
  private socketSub?: Subscription;
  private menuListSub?: Subscription;

  /** Bug 1: in-flight guard so one PERMISSIONS_UPDATE event = one refresh. */
  private permissionRefreshInFlight = false;

  isDropdownOpen = false;

  /* ---- Company switch ---- */

  /** The pending switch, held while the confirm dialog is open. */
  pendingSwitch: CompanySwitchRequest | null = null;
  isConfirmModalOpen = false;
  isSwitching = false;

  get switchFromCompany(): SwitchableCompanyView | null {
    return this.pendingSwitch?.from ?? null;
  }

  get switchToCompany(): SwitchableCompanyView | null {
    return this.pendingSwitch?.to ?? null;
  }

  /* ---- Access (user group) switch ---- */

  /** The pending access switch, held while the confirm dialog is open. */
  pendingAccessSwitch: AccessSwitchRequest | null = null;
  isAccessConfirmOpen = false;

  /** Employee code shown under each role in the Role Switch dialog. */
  employeeCode: string | null = null;

  get switchFromRole(): SwitchCard | null {
    return this.toRoleCard(this.pendingAccessSwitch?.from?.label);
  }

  get switchToRole(): SwitchCard | null {
    return this.toRoleCard(this.pendingAccessSwitch?.to.label);
  }

  /** Same person on both sides: the role name, their employee code and photo. */
  private toRoleCard(label: string | undefined): SwitchCard | null {
    if (!label) return null;
    return { name: label, subtitle: this.employeeCode ?? '', imageUrl: this.userProfile };
  }

  toggleMobileMenu(): void {
    this.sidebarService.toggleMobileMenu();
  }

  companyName = '';

  clientLogo: string | null = null;
  logoReloadAttempts = 0;
  maxLogoReloadAttempts = 2;
  userProfile: string | null = null;

  dateFormat = 'dd/MM/yyyy';
  loggedUser: any = {};

  constructor() {
    const authUser = localStorage.getItem(this.auth.AUTH_USER);
    this.loggedUser = authUser ? JSON.parse(authUser) : null;
  }

  /* ---- Global menu search ---- */

  /**
   * Searches the same permission-filtered menu list the sidebar renders from
   * (`SidebarService.menuList$`, sourced from `rolepermission/permission/:groupId`)
   * — never a separate query, so results can never show a menu the signed-in
   * user isn't actually allowed to open.
   */
  private readonly menuList = signal<MenuItem[]>([]);

  readonly searchTerm = signal<string>('');
  readonly isSearchOpen = signal<boolean>(false);
  readonly activeResultIndex = signal<number>(-1);

  readonly searchResults = computed<SearchResult[]>(() => {
    const term = this.searchTerm().trim().toLowerCase();
    if (!term) return [];
    const menus = this.menuList();
    const menuById = new Map<number, MenuItem>(menus.map((m) => [m.id, m]));
    // The menus table has duplicate rows for the same screen (e.g. two
    // "Users" rows, both /admin-master/users) - de-duplicate on the
    // destination so a screen is listed once. Distinct screens that share a
    // name (e.g. "Leave" under Requests and under Approve) are kept, and the
    // parent menu name is shown so they can be told apart.
    const seenLinks = new Set<string>();
    const results: SearchResult[] = [];
    for (const menu of menus) {
      if (!menu.link || !menu.name?.toLowerCase().includes(term)) continue;
      if (!isViewable(menu, menuById)) continue;
      const linkKey = toLinkKey(menu.link);
      if (seenLinks.has(linkKey) || isExcludedFromSearch(linkKey)) continue;
      if (sharesParentLink(menu, linkKey, menuById)) continue;
      if (menuDepth(menu, menuById) > MAX_SEARCH_MENU_DEPTH) continue;
      seenLinks.add(linkKey);
      results.push({ ...menu, parentName: menu.parentID != null ? (menuById.get(menu.parentID)?.name ?? null) : null });
      if (results.length === 8) break;
    }
    return results;
  });

  onSearchChange(term: string): void {
    this.searchTerm.set(term);
    this.activeResultIndex.set(-1);
    this.isSearchOpen.set(!!term.trim());
  }

  onSearchFocus(): void {
    if (this.searchTerm().trim()) this.isSearchOpen.set(true);
  }

  closeSearch(): void {
    this.isSearchOpen.set(false);
    this.activeResultIndex.set(-1);
  }

  isSearchResultActive(index: number): boolean {
    return this.activeResultIndex() === index;
  }

  /** Navigates like clicking the same item in the sidebar would. */
  selectSearchResult(item: SearchResult): void {
    if (!item?.link) return;
    this.auth.selectedMenuID = item.id;
    localStorage.setItem('activeMenuName', item.name);
    localStorage.setItem('activeMenu', JSON.stringify(item));
    this.router.navigate([item.link]);
    this.searchTerm.set('');
    this.closeSearch();
  }

  onSearchKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      this.closeSearch();
      return;
    }
    if (!this.isSearchOpen()) return;

    const results = this.searchResults();
    if (!results.length) return;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.activeResultIndex.set((this.activeResultIndex() + 1) % results.length);
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.activeResultIndex.set(
          this.activeResultIndex() <= 0 ? results.length - 1 : this.activeResultIndex() - 1,
        );
        break;
      case 'Enter': {
        event.preventDefault();
        const target = results[this.activeResultIndex()] ?? results[0];
        this.selectSearchResult(target);
        break;
      }
      default:
        break;
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    if (this.isSearchOpen() && !this.elementRef.nativeElement.contains(event.target)) {
      this.closeSearch();
    }
  }

  get isSuperAdmin(): boolean {
    return this.loggedUser?.role_type === 'super_user' || this.loggedUser?.role_type === 'super_admin';
  }

  get avatarRole(): AvatarRole {
    return this.loggedUser?.role_type === 'admin' || this.loggedUser?.role_type === 'sub_admin'
      ? this.loggedUser.role_type
      : 'employee';
  }

  private hasValidCompanyId(companyID: any): boolean {
    return !!this.resolveCompanyId(companyID);
  }

  /** `companyID` can come back as a single id or an array (multi-company admins) — normalize to one id. */
  private resolveCompanyId(companyID: any): any {
    if (Array.isArray(companyID)) return companyID.find((id) => !!id);
    return companyID;
  }

  ngOnInit(): void {
    this.getCompanyDetails();
    this.initSocketNotifications();

    this.menuListSub = this.sidebarService.menuList$.subscribe((menus) => {
      this.menuList.set(Array.isArray(menus) ? menus : []);
    });
  }

  ngOnDestroy(): void {
    this.socketSub?.unsubscribe();
    this.menuListSub?.unsubscribe();
  }

  /**
   * Bug 8689: notification-service socket rooms are keyed by a bare integer
   * id, and `hrms.users.id` (admin / client) overlaps `hrms.employee_login.id`
   * (employee). A FORCE_LOGOUT aimed at an employee login was also delivered to
   * a colliding admin/client login and logged them out. The server now stamps
   * every FORCE_LOGOUT with `targetLoginType` and, for a single-company
   * inactivation, `companyId`. Only act when the event is genuinely for THIS
   * session:
   *   - identity space must match (employee vs admin/client), and
   *   - for `company_deactivated`, the deactivated company must be the one this
   *     tab is currently working in — a multi-company employee keeps their
   *     other-company session.
   * Legacy events with no `targetLoginType` are still honoured (fail-open) so
   * an older server can't leave a revoked session stranded.
   */
  private isForceLogoutForThisSession(data: any): boolean {
    const user = this.auth.currentUser() || this.loggedUser;

    if (data?.targetLoginType) {
      const mySessionType =
        user?.role_type === 'employee' || user?.role_type === 'super_user' ? 'employee' : 'admin';
      if (data.targetLoginType !== mySessionType) {
        return false;
      }
    }

    if (data?.reason === 'company_deactivated' && data?.companyId != null) {
      const activeCompanyId = this.resolveCompanyId(user?.companyID);
      if (activeCompanyId != null && String(activeCompanyId) !== String(data.companyId)) {
        // A different one of my companies was deactivated — my current
        // session stays; just refresh the company switcher so the dead one
        // drops off.
        this.getCompanyDetails();
        return false;
      }
    }

    return true;
  }

  private initSocketNotifications(): void {
    const user = this.auth.currentUser() || this.loggedUser;
    const ids = [user?.id, user?.userID, user?.employee_login_id].filter((id) => !!id);
    ids.forEach((id) => this.bulkSocket.joinRoom(id!));

    this.socketSub = this.bulkSocket.getMessages().subscribe((msg: any) => {
      const data = msg?.data || msg;

      // Bug 8256 / 8689: real-time force logout. The account or the company
      // was inactivated; the server already revoked the session, this makes
      // an idle client log out immediately instead of on its next request.
      if (data?.event === 'FORCE_LOGOUT') {
        if (!this.isForceLogoutForThisSession(data)) {
          return;
        }
        this.socketSub?.unsubscribe();
        const message =
          data.reason === 'company_deactivated'
            ? 'Your company account has been deactivated. Please contact your administrator.'
            : data.reason === 'client_deactivated'
              ? 'Your organisation account has been deactivated. Please contact your administrator.'
              : 'Your account has been deactivated. Please contact your administrator.';
        this.auth.clearSession(message);
        this.router.navigateByUrl('/login');
        return;
      }

      // An admin just saved Roles & Permissions for one of this user's groups; re-fetch the authoritative
      // permission list through the existing Auth.getPermission flow — the
      // same refresh the self-group R&P save already uses — and let the
      // existing menuList$ / sidebar reactive chain update the UI. Targeting
      // already happened server-side (backend resolved the group's members),
      // so no client-side group matching. No logout, no reload, no
      // navigation, no session/token change.
      if (data?.event === 'PERMISSIONS_UPDATE') {
        this.refreshPermissionsFromEvent();
        return;
      }

      // The socket beat the fallback watch to this result, so stop it before its next check —
      // otherwise it raises a second toast/banner for the same upload once it re-checks and finds
      // the same new row. If the socket never arrives, the fallback watch is untouched and still
      // delivers the result on its own (see NotificationsService.watchForBackgroundResult).
      this.notifyService.stopWatching();
      this.notifyService.triggerNotificationUpdate();

      const successCount = Number(data?.successCount || 0);
      const failureCount = Number(data?.failureCount || 0);
      // See NotificationsService.notifyUploadResult() for why every one of
      // these variants is checked — the backend names this field differently
      // depending on which flow wrote the notification.
      const reportUrl: string =
        data?.url ||
        data?.errorReportUrl ||
        data?.failedFileUrl ||
        data?.error_file_url ||
        data?.errorFileUrl ||
        data?.download_url ||
        data?.downloadUrl ||
        '';
      const reportAction: [string?, (() => void)?] = reportUrl
        ? ['Download Error File', () => this.notifyService.downloadReport(reportUrl)]
        : [];

      if (msg?.moduleType !== 'PAYROLL' && !data?.message?.startsWith?.('Payroll')) {
        if (data?.nodata) {
          this.toaster.info('No data found', 'The uploaded file did not contain any importable rows.');
        } else if (data?.validationOnly) {
          const summary = `${successCount} row(s) would import, ${failureCount} row(s) would fail. Nothing was saved.`;
          if (failureCount > 0) {
            this.toaster.warning('Validation completed with errors', summary, 'top-right', ...reportAction);
          } else {
            this.toaster.success('Validation passed', summary);
          }
        } else if (successCount > 0 && failureCount <= 0) {
          const message = `${successCount} record(s) has been created successfully`;
          this.toaster.success('Success', message);
          this.notifyService.showUploadBanner({ variant: 'success', title: 'Success', message });
        } else if (successCount <= 0 && failureCount > 0) {
          // Row-level validation text belongs in the error file only; the banner shows the standard summary.
          if (/validation failed/i.test(data?.message || '')) {
            const summary = `${successCount} record(s) has been created successfully, ${failureCount} failed due to errors`;
            this.toaster.warning('Upload completed with errors', summary, 'top-right', ...reportAction);
            this.notifyService.showUploadBanner({
              variant: 'warning',
              title: 'Upload Completed with Errors',
              message: summary,
              reportUrl,
            });
            return;
          }
          const message = data?.message || 'Upload has been failed due to some technical issue.';
          if (reportUrl.includes('assets_inventory_failed.xlsx')) {
            this.toaster.error('Failed', message, 'top-right');
          } else {
            this.toaster.error('Failed', message, 'top-right', ...reportAction);
          }
          // A failed upload's toast auto-dismisses in a few seconds and is easy to miss — the
          // banner (rendered once in MainLayout) stays up until the user closes it.
          this.notifyService.showUploadBanner({ variant: 'error', title: 'Failed', message, reportUrl });
        } else if (successCount > 0 && failureCount > 0) {
          const message = `${successCount} record(s) has been created successfully, ${failureCount} failed due to some field mapping error`;
          this.toaster.warning('Upload completed with errors', message, 'top-right', ...reportAction);
          this.notifyService.showUploadBanner({
            variant: 'warning',
            title: 'Upload Completed with Errors',
            message,
            reportUrl,
          });
        }
      }
    });
  }

  /**
   * Bug 1: handler for the backend PERMISSIONS_UPDATE socket event.
   *
   * One event → one refresh: an in-flight refresh swallows any further event
   * until it completes (the list fetched at completion is already the latest,
   * so an extra fetch would be redundant). Uses only the existing
   * Auth.getPermission(group_id) primitive; the sidebar/sub-sidebar react
   * through their existing menuList$ subscriptions. Invalid/missing group_id
   * does nothing — never degrades to a group-0 "full catalogue" request.
   */
  private refreshPermissionsFromEvent(): void {
    if (this.permissionRefreshInFlight) return;

    const user = this.auth.currentUser() || this.loggedUser;
    const groupId = Number(user?.group_id);
    if (!Number.isInteger(groupId) || groupId <= 0) {
      this.logger.warn('[PERM] PERMISSIONS_UPDATE ignored — no valid group_id on the current session');
      return;
    }

    this.permissionRefreshInFlight = true;
    this.auth.getPermission(groupId).subscribe({
      next: () => {
        this.permissionRefreshInFlight = false;
      },
      error: () => {
        this.permissionRefreshInFlight = false;
      },
    });
  }

  onNotificationsOpened(): void {
    this.userProfileRef?.closeDropdown();
    this.companySwitcherRef?.closeDropdown();
    this.accessSwitcherRef?.closeDropdown();
  }

  onUserDropdownOpened(): void {
    this.notificationsRef?.closePanel();
    this.companySwitcherRef?.closeDropdown();
    this.accessSwitcherRef?.closeDropdown();
  }

  onCompanySwitcherOpened(): void {
    this.notificationsRef?.closePanel();
    this.userProfileRef?.closeDropdown();
    this.accessSwitcherRef?.closeDropdown();
  }

  onAccessSwitcherOpened(): void {
    this.notificationsRef?.closePanel();
    this.userProfileRef?.closeDropdown();
    this.companySwitcherRef?.closeDropdown();
  }

  /* ------------------------------------------------------------------ */
  /* Company switch                                                      */
  /* ------------------------------------------------------------------ */

  /** A company was picked in the switcher — confirm before doing anything. */
  onCompanySelected(request: CompanySwitchRequest): void {
    this.pendingSwitch = request;
    this.isConfirmModalOpen = true;
  }

  onCancelCompanySwitch(): void {
    this.isConfirmModalOpen = false;
    this.pendingSwitch = null;
  }

  /**
   * Performs the switch: exchange the current token for one bound to the target
   * company, then reload so every module re-reads its data under the new tenant.
   *
   * Nothing is mutated until the server has accepted the switch — on any failure
   * the session, the stored token and the active company are all left untouched,
   * so a refused switch cannot strand the UI on a company the user cannot see.
   */
  async onConfirmCompanySwitch(): Promise<void> {
    const target = this.pendingSwitch?.to;
    const user = this.auth.currentUser();
    if (!target || !user || this.isSwitching) return;

    this.isSwitching = true;

    try {
      const payload: GenerateTokenRequest = {
        token: (user as { accessToken?: string })?.accessToken ?? '',
        company_uuid: target.uuid,
      };

      const newToken = await lastValueFrom(this.commonService.generateToken(payload));

      this.auth.updateUser({
        ...user,
        companyID: target.id,
        accessToken: newToken,
      } as typeof user);

      // Restored as the active company on the next sign-in.
      localStorage.setItem('default_company', String(target.id));

      // Company-scoped caches must not survive the tenant boundary. The menu
      // cache is already reset inside `Auth.updateUser`, so only the parameter
      // cache needs clearing here.
      this.commonService.clearParametersCache();

      this.isConfirmModalOpen = false;
      this.pendingSwitch = null;

      // A full reload is intentional: it is the only way to guarantee no
      // component is still holding data resolved under the previous company.
      await this.router.navigate(['/dashboard']);
      window.location.reload();
    } catch (err: unknown) {
      this.handleSwitchError(err);
    } finally {
      this.isSwitching = false;
    }
  }

  /**
   * Surfaces a switch failure without changing the active company.
   *
   * 403 covers two distinct cases the server reports separately — not authorized
   * for the entity, and the entity being deactivated — so its message is shown
   * verbatim rather than flattened into a generic error.
   */
  private handleSwitchError(err: unknown): void {
    const error = err as { status?: number; error?: { message?: string } };
    const serverMessage = error?.error?.message;

    if (error?.status === 403) {
      this.toaster.error(
        serverMessage || 'You are not authorized to access this company.',
      );
    } else if (error?.status === 400 || error?.status === 404) {
      this.toaster.error(
        serverMessage || 'That company is no longer available. Please refresh and try again.',
      );
    } else {
      this.toaster.error('Could not switch company. Please try again.');
    }

    this.logger.error('Company switch failed', err);

    this.isConfirmModalOpen = false;
    this.pendingSwitch = null;
  }

  /* ------------------------------------------------------------------ */
  /* Access (user group) switch                                          */
  /* ------------------------------------------------------------------ */

  /** A group was picked in the access switcher - confirm before doing anything. */
  onAccessSelected(request: AccessSwitchRequest): void {
    this.pendingAccessSwitch = request;
    this.isAccessConfirmOpen = true;
  }

  onCancelAccessSwitch(): void {
    if (this.isSwitching) return;
    this.isAccessConfirmOpen = false;
    this.pendingAccessSwitch = null;
  }

  /**
   * Performs the access switch: `Auth.switchGroup` exchanges the token and
   * replaces the stored user (clearing the menu cache) only once the server has
   * accepted it, so a refused switch leaves the active access untouched. Then,
   * like the company switch, drop the parameter cache and reload on the
   * dashboard so every module re-reads its data under the new access.
   */
  async onConfirmAccessSwitch(): Promise<void> {
    const target = this.pendingAccessSwitch?.to;
    if (!target || this.isSwitching) return;

    this.isSwitching = true;

    try {
      await lastValueFrom(this.auth.switchGroup(target.group_id));
    } catch (err: unknown) {
      this.handleAccessSwitchError(err);
      this.isSwitching = false;
      return;
    }

    // The switch is stored; whatever happens next, the page must reload so the
    // session is consistent - a navigation hiccup is not a failed switch.
    this.commonService.clearParametersCache();
    this.isAccessConfirmOpen = false;
    this.pendingAccessSwitch = null;
    await this.router
      .navigate(['/dashboard'])
      .catch((err: unknown) => this.logger.error('Navigation after access switch failed', err));
    window.location.reload();
  }

  /** Surfaces an access switch failure; a 403 shows the server's own message. */
  private handleAccessSwitchError(err: unknown): void {
    if (err instanceof GroupSwitchIncompleteError) {
      // The session was already cleared; the user has to sign in again.
      this.toaster.error(err.message);
      this.isAccessConfirmOpen = false;
      this.pendingAccessSwitch = null;
      this.router.navigateByUrl('/login');
      return;
    }

    const error = err as { status?: number; error?: { message?: string } };
    const serverMessage = error?.error?.message;

    if (error?.status === 403) {
      this.toaster.error(serverMessage || 'You do not have access to this user group.');
    } else {
      this.toaster.error(serverMessage || 'Could not switch access. Please try again.');
    }

    this.logger.error('Access switch failed', err);

    this.isAccessConfirmOpen = false;
    this.pendingAccessSwitch = null;
  }

  private async getCompanyDetails(): Promise<void> {
    const user = this.loggedUser;
    if (!user) return;

    this.clientLogo = null;
    this.userProfile = null;

    const isAdminOrSubAdmin = user.role_type === 'admin' || user.role_type === 'sub_admin';

    if (isAdminOrSubAdmin) {
      if (this.hasValidCompanyId(user.companyID)) {
        try {
          const companyId = this.resolveCompanyId(user.companyID);
          const companiesResp = await lastValueFrom(this.commonService.getClientCompanyList());
          const companies = companiesResp?.data || [];
          const foundCompany = companies.find((c: any) => c.id == companyId || c.uuid == companyId);
          const companyUuid = foundCompany?.uuid || companyId;

          const response = await lastValueFrom(this.commonService.getCompanybyID(companyUuid));
          this.companyName = response?.data?.company_name || '';

          if (response?.data?.logo) {
            this.loadClientLogo(response.data.logo);
          } else {
            // No company logo, fallback to admin profile logo
            if (user.userID) {
              this.loadAdminLogo(user.userID);
            }
          }
        } catch (err) {
          console.error(err);
          // Fallback to admin profile logo on error
          if (user.userID) {
            this.loadAdminLogo(user.userID);
          }
        }
      } else {
        // No company ID, load admin profile logo
        if (user.userID) {
          this.loadAdminLogo(user.userID);
        }
      }

      if (this.hasValidCompanyId(user.companyID)) {
        this.commonService.getCompanyParameters(this.resolveCompanyId(user.companyID)).subscribe({
          next: (resp: any) => {
            if (resp?.data?.date_format) {
              this.dateFormat = this.commonService.convertPrimeNGToAngularFormat(
                resp.data.date_format,
              );
            }
          },
          error: (err) => console.error(err),
        });
      }
    } else if (user.role_type === 'super_user' || user.role_type === 'employee') {
      if (user.userID) {
        this.loadEmployeePhoto(user.userID);
      }

      if (this.hasValidCompanyId(user.companyID)) {
        const companyId = this.resolveCompanyId(user.companyID);
        this.commonService.getClientCompanyList().subscribe({
          next: (companiesResp) => {
            const companies = companiesResp?.data || [];
            const foundCompany = companies.find(
              (c: any) => c.id == companyId || c.uuid == companyId,
            );
            const companyUuid = foundCompany?.uuid || companyId;

            this.commonService.getCompanybyID(companyUuid).subscribe({
              next: (resp) => {
                this.companyName = resp?.data?.company_name || '';
                if (resp?.data?.logo) {
                  this.loadClientLogo(resp.data.logo);
                }
              },
              error: (err) => console.error(err),
            });
          },
          error: (err) => console.error(err),
        });

        this.commonService.getCompanyParameters(this.resolveCompanyId(user.companyID)).subscribe({
          next: (resp: any) => {
            if (resp?.data?.date_format) {
              this.dateFormat = this.commonService.convertPrimeNGToAngularFormat(
                resp.data.date_format,
              );
            }
          },
          error: (err) => console.error(err),
        });
      }
    }
  }

  /** Admin/sub-admin: fetch their account record via viewadmin, which carries their own logo */
  private loadAdminLogo(userID: any): void {
    this.commonService.getAdminById(userID).subscribe({
      next: (resp: any) => {
        if (resp?.data?.logo) {
          this.loadClientLogo(resp.data.logo);
        }
      },
      error: (err) => console.error('Error loading admin logo:', err),
    });
  }

  /** Employee: fetch their record fresh to get the current photo key */
  private loadEmployeePhoto(userID: any): void {
    this.commonService.getEmployeeById(userID).subscribe({
      next: (resp: any) => {
        this.employeeCode = resp?.data?.employee?.employeecode ?? null;
        if (resp?.data?.employee?.photo) {
          this.loadUserAvatar(resp.data.employee.photo);
        }
      },
      error: (err) => console.error('Error loading employee photo:', err),
    });
  }

  private loadClientLogo(key: string): void {
    // getFile() already encodeURIComponent()s internally — encoding here too
    // double-encodes the S3 key and 500s (see CompanySwitcher.hydrateLogos for
    // the full explanation). This ran on every page load, including the
    // post-switch reload, so it stacked with the switcher's own logo fetches
    // toward the same circuit breaker.
    this.commonService.getFile(key).subscribe({
      next: (blob: any) => {
        this.clientLogo = URL.createObjectURL(blob);
      },
      error: (err) => console.error('Error loading client logo:', err),
    });
  }

  private loadUserAvatar(key: string): void {
    this.commonService.getFile(key).subscribe({
      next: (blob: any) => {
        this.userProfile = URL.createObjectURL(blob);
      },
      error: (err) => {
        console.error('Error loading user avatar:', err);
        this.userProfile = null;
      },
    });
  }

  reloadLogo(): void {
    if (this.logoReloadAttempts >= this.maxLogoReloadAttempts) return;
    this.logoReloadAttempts++;
    if (this.loggedUser?.icon) {
      this.loadUserAvatar(this.loggedUser.icon);
    }
  }
}
